import type { SupabaseClient } from '@supabase/supabase-js';
import { sendVarioMessage } from '../vario-iframe.js';

interface UninstallContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { tenantId: string };
}

interface UninstallResult {
  ok: boolean;
  removed_credential_secret: boolean;
  removed_offline_token_secret: boolean;
}

// Scoped Karten-Design für den Gambio-Uninstall-Dialog. Bewusst self-contained (eigener
// `.gambio-uninstall`-Scope, keine Abhängigkeit von install.ts), passt aber optisch zur
// Install-Karte (go-brand-Tokens, zentrierte Karte). Auto-Grow-Marker html.gambio-autogrow
// + main.ts 'auto'-Modus sorgen dafür, dass das iframe = Karteninhalt ist und Varios OK-Button
// direkt darunter sitzt (vorher: fixer 900px-Korridor → kurze Meldung oben, OK weit unten).
const STYLE_ID = 'gambio-uninstall-styles';

const STYLES = `
.gambio-uninstall{--vario-green:#84B933;--vario-green-dark:#6FA028;--vario-green-tint:#EFF6E2;
  --ink:#1A1A1A;--muted:#737373;--surface:#FFFFFF;--field:#F1F1F3;--danger:#E5484D;
  font-family:'Outfit',system-ui,sans-serif;color:var(--ink);}
.gambio-uninstall *{box-sizing:border-box;}
.gambio-uninstall .stage{display:flex;align-items:center;justify-content:center;padding:20px 16px;}
.gambio-uninstall .card{width:100%;max-width:520px;background:var(--surface);border-radius:16px;
  box-shadow:0 1px 2px rgba(16,24,40,.04),0 18px 44px -20px rgba(16,24,40,.22);
  padding:40px 34px 34px;text-align:center;}
.gambio-uninstall .u-ic{width:60px;height:60px;border-radius:50%;background:var(--field);
  display:flex;align-items:center;justify-content:center;margin:0 auto 18px;}
.gambio-uninstall .u-ic svg{width:28px;height:28px;}
.gambio-uninstall .u-ic.ok{background:var(--vario-green-tint);}
.gambio-uninstall .u-ic.ok svg{color:var(--vario-green-dark);}
.gambio-uninstall .u-ic.error{background:#FDECEC;}
.gambio-uninstall .u-ic.error svg{color:var(--danger);}
.gambio-uninstall .big-spin{width:28px;height:28px;border:3px solid #E2E2E6;border-top-color:var(--vario-green);border-radius:50%;animation:gambio-uspin .7s linear infinite;}
@keyframes gambio-uspin{to{transform:rotate(360deg);}}
.gambio-uninstall h2{font-size:20px;font-weight:700;letter-spacing:-0.01em;margin:0 0 8px;}
.gambio-uninstall .status{font-size:13.5px;color:var(--muted);line-height:1.55;margin:0 auto;max-width:400px;}
.gambio-uninstall .status.error{color:var(--danger);}

/* Auto-Grow (Marker html.gambio-autogrow, gesetzt in renderUninstall): löst die bridge-§16-
   100%-Kette, damit body.scrollHeight den Karteninhalt statt der Viewport-Höhe meldet. */
html.gambio-autogrow, html.gambio-autogrow body, html.gambio-autogrow main#app{height:auto;overflow:visible;}
html.gambio-autogrow main#app > section#content{flex:0 0 auto;}
`;

const ICON = {
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>',
  x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>',
};

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}

/**
 * Vario öffnet diesen Slot nach Klick auf "Deinstallieren". Flow (analog Kaufland):
 *   1. Server-State abräumen über den gambio_uninstall-RPC. Der löscht die
 *      gambio-marketplace_connection des Tenants UND shreddert beide Vault-Secrets
 *      (Credentials + Vario-Offline-Token) best-effort. Gambio ist ein Einzelshop,
 *      hat also keine Storefronts/Folge-State.
 *   2. `{ requestUninstall: true }` an den Vario-Host senden.
 *   3. Auf `{ uninstallState: true|false }` vom Host warten.
 */
export async function renderUninstall(ctx: UninstallContext) {
  ensureStyles();
  // Auto-Grow: iframe = Inhaltshöhe (Karte), Vario scrollt außen (Marker + main.ts 'auto').
  document.documentElement.classList.add('gambio-autogrow');
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="gambio-uninstall">
      <div class="stage">
        <div class="card">
          <div class="u-ic" id="uninstall-icon"><span class="big-spin"></span></div>
          <h2 id="uninstall-line">Gambio-App wird deinstalliert …</h2>
          <p class="status" id="uninstall-status" aria-live="polite"></p>
        </div>
      </div>
    </div>
  `;

  const iconEl = ctx.contentEl.querySelector<HTMLElement>('#uninstall-icon')!;
  const lineEl = ctx.contentEl.querySelector<HTMLElement>('#uninstall-line')!;
  const statusEl = ctx.contentEl.querySelector<HTMLParagraphElement>('#uninstall-status')!;

  function setIcon(state: 'spin' | 'ok' | 'error') {
    if (state === 'spin') {
      iconEl.className = 'u-ic';
      iconEl.innerHTML = '<span class="big-spin"></span>';
    } else if (state === 'ok') {
      iconEl.className = 'u-ic ok';
      iconEl.innerHTML = ICON.check;
    } else {
      iconEl.className = 'u-ic error';
      iconEl.innerHTML = ICON.x;
    }
  }

  function setStatus(text: string, kind: 'info' | 'ok' | 'error' = 'info') {
    statusEl.textContent = text;
    statusEl.className = kind === 'error' ? 'status error' : 'status';
  }

  // ---- 1. Server-side teardown -------------------------------------------
  let serverOk = true;
  try {
    const { data, error } = await ctx.supabase.rpc('gambio_uninstall');
    if (error) {
      serverOk = false;
      setStatus(`Server-Cleanup fehlgeschlagen: ${error.message}`, 'error');
    } else {
      const r = (Array.isArray(data) ? data[0] : data) as UninstallResult | undefined;
      if (r?.ok) {
        setStatus('Gambio-Verbindung deaktiviert (Konfiguration bleibt erhalten).', 'info');
      } else {
        setStatus('Keine Verbindung gespeichert - Vario-Status wird zurückgesetzt.', 'info');
      }
    }
  } catch (e) {
    serverOk = false;
    setStatus(`Server-Cleanup-Ausnahme: ${(e as Error).message}`, 'error');
  }

  // ---- 2. Tell Vario to remove the app, wait for confirmation ------------
  const ok = await requestVarioUninstall();

  if (ok) {
    setIcon('ok');
    lineEl.textContent = 'Gambio-App wurde deinstalliert';
    setStatus(serverOk ? '' : 'Hinweis: Vario-Seite ist deinstalliert, aber unser Server-Cleanup hat einen Fehler gemeldet.', serverOk ? 'info' : 'error');
  } else {
    setIcon('error');
    lineEl.textContent = 'Vario hat die Deinstallation abgelehnt';
    setStatus('Bitte versuche es später erneut oder kontaktiere den Support.', 'error');
  }
}

function requestVarioUninstall(): Promise<boolean> {
  return new Promise((resolve) => {
    const listener = (e: MessageEvent) => {
      const data = e.data as { uninstallState?: boolean } | null;
      if (!data || typeof data !== 'object' || typeof data.uninstallState !== 'boolean') return;
      window.removeEventListener('message', listener);
      resolve(data.uninstallState);
    };
    window.addEventListener('message', listener);

    // 30s safety timeout - if Vario never answers, fail closed.
    setTimeout(() => {
      window.removeEventListener('message', listener);
      resolve(false);
    }, 30_000);

    sendVarioMessage({ requestUninstall: true });
  });
}
